import { useEffect, useState } from "react";
import { Box, Typography, Card, CardContent, Grid, Alert } from "@mui/material";
import { Add, Visibility } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "@/context/AuthContext";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { firestore } from "@/firebase";
import { raveConverter } from "@/converters";

export const LiteRaveHome = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [recentRaves, setRecentRaves] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/lite-raves/signup");
      return;
    }

    const fetchRecentRaves = async () => {
      try {
        const ravesRef = collection(firestore, `company/${user.company}/raves`);
        const q = query(
          ravesRef.withConverter(raveConverter),
          where("sender.id", "==", user.id),
          where("type", "==", "liteRaves"),
          orderBy("createdAt", "desc"),
          limit(5),
        );

        const snapshot = await getDocs(q);
        const raves = snapshot.docs.map((doc) => doc.data());
        setRecentRaves(raves);
      } catch (error) {
        console.error("Error fetching raves:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentRaves();
  }, [user, navigate]);

  return (
    <Box>
      <Typography variant="h3" sx={{ mb: 1 }}>
        Welcome to Lite Raves
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Create simple, powerful messages to share with your team.
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              cursor: "pointer",
              transition: "transform 0.2s",
              "&:hover": { transform: "translateY(-4px)" },
            }}
            onClick={() => navigate("/lite-raves/create")}
          >
            <CardContent sx={{ textAlign: "center", p: 4 }}>
              <Add sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 1 }}>
                Create a Rave
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Share a message, image, video, or audio with your team
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              cursor: "pointer",
              transition: "transform 0.2s",
              "&:hover": { transform: "translateY(-4px)" },
            }}
            onClick={() => navigate("/lite-raves/my-raves")}
          >
            <CardContent sx={{ textAlign: "center", p: 4 }}>
              <Visibility sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 1 }}>
                View My Raves
              </Typography>
              <Typography variant="body2" color="text.secondary">
                See all the raves you've created
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {!loading && recentRaves.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Recent Raves
          </Typography>
          <Grid container spacing={2}>
            {recentRaves.map((rave) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={rave.id}>
                <Card
                  sx={{
                    cursor: "pointer",
                    "&:hover": { boxShadow: 3 },
                  }}
                  onClick={() => navigate(`/lite-raves/${rave.id}`)}
                >
                  <CardContent>
                    <Typography variant="h6" noWrap>
                      {rave.title || "Untitled"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {rave.description || "No description"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {!loading && recentRaves.length === 0 && (
        <Alert severity="info" sx={{ mt: 4 }}>
          You haven't created any lite raves yet. Click the "Create a Rave"
          button above to get started!
        </Alert>
      )}
    </Box>
  );
};
